import { memo, ReactNode } from 'react';
import { A11y, Mousewheel, Scrollbar } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Flex, Stack, useBreakpointValue, useMediaQuery } from '@chakra-ui/react';
import type { BreakpointValue } from '@udacity/chakra-uds-theme';

import { EnterpriseResourceContainer } from './enterprise-resource-container';

import 'swiper/css';
import 'swiper/css/scrollbar';

export type SlidesPerViewValue = number | 'auto';

export type CarouselProps = {
  items: ReactNode[];
  slidesPerView?: SlidesPerViewValue | BreakpointValue<SlidesPerViewValue>;
};

export const EnterpriseResourceScrollContainer = memo(function ScrollContainer({
  items,
  slidesPerView = 'auto',
}: CarouselProps) {
  const [atMobileWidth] = useMediaQuery(`(max-width: 640px)`, { ssr: true });
  const slidesPerViewBreakPointValues = typeof slidesPerView === 'object' ? slidesPerView : { base: slidesPerView };
  const slidesPerViewValue = useBreakpointValue(slidesPerViewBreakPointValues, { ssr: true });

  const scrollBarColor = 'var(--chakra-colors-blue-500)';

  return (
    <Stack alignItems='center' position='relative' spacing={4} zIndex='base'>
      <EnterpriseResourceContainer
        display={{ base: 'none', lg: 'block' }}
        sx={{
          '.uds-carousel .swiper-scrollbar-drag': {
            backgroundColor: scrollBarColor,
          },
          '.uds-carousel': {
            display: 'flex',
            justifyContent: 'center',
            paddingTop: 0,
            paddingBottom: `49px`,
          },
          '.uds-carousel .swiper-scrollbar': {
            top: 'unset',
            right: 'unset',
            left: 'unset',
            bottom: 0,
          },
          '.uds-carousel--rtl .swiper-scrollbar': {
            right: 'unset',
            left: 'unset',
          },
        }}
      >
        <Swiper
          key={`${slidesPerViewValue}`}
          className={`uds-carousel`}
          cssMode={atMobileWidth}
          direction='horizontal'
          freeMode={true}
          grabCursor={true}
          modules={[A11y, Scrollbar, Mousewheel]}
          mousewheel={true}
          scrollbar={{ draggable: true }}
          slidesPerView={slidesPerViewValue ?? 'auto'}
          spaceBetween={32}
          threshold={20}
        >
          {items.map((item, i) => (
            <SwiperSlide key={i} style={{ height: 'auto' }}>
              {item}
            </SwiperSlide>
          ))}
        </Swiper>
      </EnterpriseResourceContainer>

      <EnterpriseResourceContainer display={{ base: 'block', lg: 'none' }}>
        <Flex flexWrap='wrap' gap='32px' overflowX='auto' width='100%'>
          {items}
        </Flex>
      </EnterpriseResourceContainer>
    </Stack>
  );
});
